import React from 'react';
import { SimplifiedChampion, StatKeys2 } from "../../../../../gameTypes";
import './StatsDisplayExpanded.css';

interface ChampionStatsProps {
    champion: SimplifiedChampion;
  }
  
  const statsToDisplay: { key: StatKeys2; label: string; format?: (v: number) => string }[] = [
    { key: 'health', label: 'Health' },
    { key: 'healthRegen', label: 'Health Regen' },
    { key: 'mana', label: 'Mana' },
    { key: 'manaRegen', label: 'Mana Regen' },
    { key: 'attackDamage', label: 'Attack Damage' },
    { key: 'abilityPower', label: 'Ability Power' },
    { key: 'armor', label: 'Armor' },
    { key: 'magicResistance', label: 'Magic Resistance' },
    { key: 'attackSpeed', label: 'Attack Speed', format: v => v.toFixed(3) },
    { key: 'abilityHaste', label: 'Ability Haste' },
    { key: 'movementSpeed', label: 'Movement Speed' },
    { key: 'criticalStrikeChance', label: 'Crit Chance', format: v => `${Math.round(v)}%` },
    { key: 'criticalStrikeDamage', label: 'Crit Damage', format: v => `${Math.round(v)}%` },
    { key: 'attackRange', label: 'Attack Range' },
    { key: 'lethality', label: 'Lethality' },
    { key: 'armorPenetrationPercent', label: 'Armor Pen %', format: v => `${Math.round(v)}%` },
    { key: 'magicPenetration', label: 'Magic Pen' },
    { key: 'magicPenetrationPercent', label: 'Magic Pen %', format: v => `${Math.round(v)}%` },
    { key: 'lifeSteal', label: 'Life Steal', format: v => `${Math.round(v)}%` },
    { key: 'omnivamp', label: 'Omnivamp', format: v => `${Math.round(v)}%` },
    { key: 'healShieldPower', label: 'Heal & Shield Power', format: v => `${Math.round(v)}%` },
    { key: 'tenacity', label: 'Tenacity', format: v => `${Math.round(v)}%` },
    { key: 'ultimateHaste', label: 'Ultimate Haste' },
    { key: 'basicAbilityHaste', label: 'Basic Ability Haste' },
    { key: 'itemHaste', label: 'Item Haste' },
  ];

  const formatValue = (value: number | undefined, formatter?: (v: number) => string): string => {
    if (value === undefined || value === null) return '0';
    if (formatter) return formatter(value);
    return Math.round(value * 100) / 100 + '';
  };

const StatsDisplayExpanded: React.FC<ChampionStatsProps> = ({ champion }) => {
    const { baseStats, bonusStats, totalStats, modifiers } = champion.stats;
  
    return (
      <div className="stats-expanded-container">
        <h2>
          {champion.name} (Level {champion.level})
        </h2>
  
        <h3>Stats Breakdown</h3>
        <table>
          <thead>
            <tr>
              <th>Stat</th>
              <th>Base</th>
              <th>Bonus (Flat)</th>
              <th>Bonus (%)</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {statsToDisplay.map(({ key, label, format }) => (
              <tr key={key}>
                <td>{label}</td>
                <td>{formatValue(baseStats[key], format)}</td>
                <td>{formatValue(bonusStats.flatStats[key], format)}</td>
                <td>{formatValue(bonusStats.percentStats[key], format)}</td>
                <td>{formatValue(totalStats[key], format)}</td>
              </tr>
            ))}
          </tbody>
        </table>
  
        {modifiers.length > 0 && (
          <>
            <h3>Stat Modifiers</h3>
            <table>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Stat</th>
                  <th>Type</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {modifiers.map((modifier, index) => (
                  <tr key={index}>
                    <td>{modifier.source}</td>
                    <td>{modifier.stat}</td>
                    <td>{modifier.type}</td>
                    <td>{modifier.type === 'percent' ? `${modifier.value}%` : modifier.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    );
  };
  
  export default StatsDisplayExpanded;
  